const KEYBOARD_OPEN_THRESHOLD = 120;
const MAX_FOCUS_POLL_FRAMES = 45;
const STABLE_FRAME_THRESHOLD = 2;
const STABLE_DELTA = 1;

export const VIEWPORT_SYNC_EVENT = "pilgrim:viewport-sync";

export type EffectiveViewportMetrics = {
  visibleHeight: number;
  keyboardBottomOffset: number;
  keyboardOpen: boolean;
};

let baseLayoutHeight = 0;
let baseWidth = 0;
let lockedMetrics: EffectiveViewportMetrics | null = null;
let focusPollId = 0;

const isEditableElement = (target: EventTarget | null): target is HTMLElement => {
  if (!(target instanceof HTMLElement)) {
    return false;
  }

  return (
    target instanceof HTMLInputElement ||
    target instanceof HTMLTextAreaElement ||
    target.isContentEditable
  );
};

const ensureBaseline = () => {
  if (typeof window === "undefined") {
    return;
  }

  if (baseWidth === 0) {
    baseWidth = window.innerWidth;
    baseLayoutHeight = window.innerHeight;
  }
};

const readRawMetrics = (): EffectiveViewportMetrics => {
  if (typeof window === "undefined") {
    return {
      visibleHeight: 0,
      keyboardBottomOffset: 0,
      keyboardOpen: false,
    };
  }

  ensureBaseline();

  const currentWidth = window.innerWidth;
  const currentInnerHeight = window.innerHeight;
  const viewport = window.visualViewport;
  const vvHeight = viewport?.height ?? currentInnerHeight;

  if (currentWidth !== baseWidth) {
    baseWidth = currentWidth;
    baseLayoutHeight = currentInnerHeight;
  }

  const candidateVisibleHeight = Math.min(vvHeight, currentInnerHeight);
  const baselineHeight = baseLayoutHeight || currentInnerHeight;
  const keyboardLikelyOpen =
    baselineHeight - candidateVisibleHeight > KEYBOARD_OPEN_THRESHOLD;

  if (!keyboardLikelyOpen) {
    baseWidth = currentWidth;
    baseLayoutHeight = currentInnerHeight;
  }

  const layoutHeight = baseLayoutHeight || currentInnerHeight;
  const visibleHeight = Math.min(vvHeight, layoutHeight);
  const keyboardBottomOffset = Math.max(
    0,
    layoutHeight - vvHeight - (viewport?.offsetTop ?? 0)
  );

  return {
    visibleHeight,
    keyboardBottomOffset,
    keyboardOpen:
      layoutHeight - visibleHeight > KEYBOARD_OPEN_THRESHOLD,
  };
};

const emitViewportSync = () => {
  if (typeof window === "undefined") {
    return;
  }

  window.dispatchEvent(new Event(VIEWPORT_SYNC_EVENT));
};

export const readEffectiveViewportMetrics = (): EffectiveViewportMetrics =>
  lockedMetrics ?? readRawMetrics();

export const syncDocumentViewportVars = (): EffectiveViewportMetrics => {
  const metrics = readEffectiveViewportMetrics();

  if (typeof document !== "undefined") {
    document.documentElement.style.setProperty(
      "--vvh",
      `${metrics.visibleHeight * 0.01}px`
    );
    document.documentElement.style.setProperty(
      "--keyboard-bottom-offset",
      `${metrics.keyboardBottomOffset}px`
    );
  }

  return metrics;
};

export const installViewportMetricsSync = () => {
  if (typeof window === "undefined") {
    return () => undefined;
  }

  const apply = () => {
    syncDocumentViewportVars();
    emitViewportSync();
  };

  const handleViewportChange = () => {
    apply();
  };

  const handleFocusIn = (event: FocusEvent) => {
    if (!isEditableElement(event.target)) {
      return;
    }

    lockedMetrics = readRawMetrics();
    apply();

    let frames = 0;
    let stableFrames = 0;
    let previousMetrics = readRawMetrics();
    const pollId = ++focusPollId;

    const poll = () => {
      if (pollId !== focusPollId) {
        return;
      }

      const currentMetrics = readRawMetrics();
      const isStable =
        Math.abs(currentMetrics.visibleHeight - previousMetrics.visibleHeight) <=
          STABLE_DELTA &&
        Math.abs(
          currentMetrics.keyboardBottomOffset -
            previousMetrics.keyboardBottomOffset
        ) <= STABLE_DELTA;

      stableFrames = isStable ? stableFrames + 1 : 0;
      previousMetrics = currentMetrics;
      frames += 1;

      if (
        (currentMetrics.keyboardOpen &&
          stableFrames >= STABLE_FRAME_THRESHOLD) ||
        frames >= MAX_FOCUS_POLL_FRAMES
      ) {
        lockedMetrics = null;
        apply();
        return;
      }

      requestAnimationFrame(poll);
    };

    requestAnimationFrame(poll);
  };

  const handleFocusOut = () => {
    focusPollId += 1;
    lockedMetrics = null;
    apply();
    requestAnimationFrame(apply);
    requestAnimationFrame(() => {
      requestAnimationFrame(apply);
    });
  };

  apply();

  window.addEventListener("resize", handleViewportChange);
  window.addEventListener("focusin", handleFocusIn);
  window.addEventListener("focusout", handleFocusOut);
  window.visualViewport?.addEventListener("resize", handleViewportChange);
  window.visualViewport?.addEventListener("scroll", handleViewportChange);

  return () => {
    focusPollId += 1;
    lockedMetrics = null;
    window.removeEventListener("resize", handleViewportChange);
    window.removeEventListener("focusin", handleFocusIn);
    window.removeEventListener("focusout", handleFocusOut);
    window.visualViewport?.removeEventListener("resize", handleViewportChange);
    window.visualViewport?.removeEventListener("scroll", handleViewportChange);
  };
};
